/**
 * CampaignInfo
 *
 * Details card for a testimonial campaign: title, description, creator/update
 * info, campaign image with modal, and optional YouTube explainer preview.
 */
import React, { useState } from 'react';
import { Type, AlignLeft, User, Clock, Calendar, Image, X, ZoomIn, ExternalLink, Youtube, Video } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../ui/layout/card';

export default function CampaignInfo({ campaign, userPermission }) {
  const [imageError, setImageError] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  
  // Only render if title or description exists
  if (!campaign.title && !campaign.description) {
    return null;
  }

  // Format date from timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const seconds = timestamp._seconds || timestamp.seconds;
    if (!seconds) return 'N/A';
    
    const date = new Date(seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if we should show the image
  const showImage = campaign.campaignImage && !imageError;

  // Function to extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    try {
      // Match YouTube URL patterns
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return null;
    }
  };
  
  // Check all possible property names for explainer video
  const videoUrl = campaign.explainerVideoUrl || campaign.explainerVideo || campaign.videoUrl;
  console.log('Campaign video data:', { 
    videoUrl,
    explainerVideoUrl: campaign.explainerVideoUrl,
    explainerVideo: campaign.explainerVideo,
    videoUrlProp: campaign.videoUrl,
    hasExplainerVideo: campaign.hasExplainerVideo,
    campaignKeys: Object.keys(campaign)
  });
  
  // Check if we have a YouTube explainer video
  const youtubeVideoId = getYoutubeVideoId(videoUrl);
  const hasYoutubeVideo = !!youtubeVideoId;
  
  console.log('YouTube video detected:', { youtubeVideoId, hasYoutubeVideo });

  const isValidUrl = (url) => {
    try {
      return Boolean(url && (url.startsWith('http://') || url.startsWith('https://')));
    } catch (e) {
      return false;
    }
  };

  // Image Modal Component
  const ImageModal = () => {
    if (!isImageModalOpen || !showImage) return null;
    
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80">
        <div className="relative max-w-5xl w-full">
          <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow-xl relative">
            {/* Close button inside the modal at the top right */}
            <button 
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full z-10 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="relative bg-white dark:bg-gray-800 flex items-center justify-center p-6">
              <img
                src={campaign.campaignImage}
                alt={campaign.title || "Campaign image"}
                className="w-full h-auto max-h-[80vh] object-contain"
                onError={() => setImageError(true)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // YouTube thumbnail URL with fallbacks
  const getYouTubeThumbnailUrl = (videoId) => {
    if (!videoId) return null;
    // Try high quality thumbnail first
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  return (
    <div className="my-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-medium text-gray-900 dark:text-white">Campaign Information</CardTitle>
          <CardDescription>Details about your campaign configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:gap-8">
            {campaign.title && (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Campaign Title</h2>
                </div>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{campaign.title}</p>
              </div>
            )}
            
            {campaign.description && (
              <div className="flex-1 mt-3 md:mt-0">
                <div className="flex items-center gap-2">
                  <AlignLeft className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Campaign Description</h2>
                </div>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{campaign.description}</p>
              </div>
            )}
          </div>
          
          {/* Creator and Updater Information */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex flex-col md:flex-row md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Created By</h2>
                </div>
                <div className="mt-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {campaign.createdByName || 'Unknown'}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(campaign.createdAt)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 mt-3 md:mt-0">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated By</h2>
                </div>
                <div className="mt-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {campaign.lastUpdatedByName || 'Unknown'}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(campaign.dateModified)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Media Section: Campaign Image and Explainer Video */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex flex-col md:flex-row md:gap-8">
              {/* Campaign Image */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Campaign Image</h2>
                </div>
                <div className="mt-3">
                  {showImage ? (
                    <div 
                      className="relative w-full max-w-[210px] h-[118px] overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 cursor-pointer group bg-white dark:bg-gray-800"
                      onClick={() => setIsImageModalOpen(true)}
                    >
                      <img
                        src={campaign.campaignImage}
                        alt={campaign.title || "Campaign image"}
                        className="w-full h-full object-contain"
                        onError={() => setImageError(true)}
                        onLoad={() => console.log('Campaign image loaded successfully')}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-full max-w-[210px] h-[118px] flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <Image className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Explainer Video */}
              <div className="flex-1 mt-3 md:mt-0">
                <div className="flex items-center gap-2">
                  <Youtube className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Explainer Video</h2>
                </div>
                <div className="mt-3">
                  {hasYoutubeVideo && isValidUrl(videoUrl) ? (
                    <div className="space-y-2">
                      <a 
                        href={videoUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="group"
                      >
                        <div className="relative w-full max-w-[210px] h-[118px] overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                          {!thumbnailError ? (
                            <img
                              src={getYouTubeThumbnailUrl(youtubeVideoId)}
                              alt="YouTube video thumbnail"
                              className="w-full h-full object-cover"
                              onError={() => setThumbnailError(true)}
                              onLoad={() => console.log('YouTube thumbnail loaded successfully')}
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 p-2">
                              <Video className="h-8 w-8 text-primary-500 mb-1" />
                              <p className="text-xs text-center text-gray-600 dark:text-gray-400">YouTube Video</p>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <ExternalLink className="h-6 w-6 text-white" />
                          </div>
                          {/* YouTube play button overlay */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                              <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full max-w-[210px] h-[118px] flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-gray-300 dark:text-gray-600">
                        <polygon points="23 7 16 12 23 17 23 7" />
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Render the image modal */}
      <ImageModal />
    </div>
  );
}